import React from "react";
import LegalLayout from "@/components/LegalLayout";
import { useI18n } from "@/context/I18nContext";

export default function Privacy() {
  const { t, lang } = useI18n();
  return (
    <LegalLayout
      kicker={t("legal.privacy_kicker")}
      title={t("legal.privacy_title")}
      lastUpdated={lang === "da" ? "Maj 2026" : "May 2026"}
    >
      {lang === "da" ? <DanishPrivacy /> : <EnglishPrivacy />}
    </LegalLayout>
  );
}

function EnglishPrivacy() {
  return (
    <>
      <p>
        Welcome to TeamFit. Your privacy is important to us. This Privacy Policy explains how TeamFit collects, uses, stores, and protects your information when you use our mobile app, website, and related services.
      </p>
      <p>By using TeamFit, you agree to this Privacy Policy.</p>

      <h2>1. Information We Collect</h2>

      <h3>Account Information</h3>
      <ul>
        <li>Name</li>
        <li>Email address</li>
        <li>Profile picture</li>
        <li>Age</li>
        <li>Height and weight (optional)</li>
        <li>Fitness level and sports interests</li>
      </ul>

      <h3>Login Information</h3>
      <p>When signing in using:</p>
      <ul>
        <li>Apple Login</li>
        <li>Google Login</li>
      </ul>
      <p>we may receive basic profile information from those providers.</p>

      <h3>Location Information</h3>
      <p>We collect:</p>
      <ul>
        <li>GPS location</li>
        <li>Live location during sessions (if enabled)</li>
        <li>Route/location information</li>
      </ul>
      <p>This helps users create activities, find nearby sessions, and navigate routes.</p>

      <h3>User Content</h3>
      <p>Users may upload:</p>
      <ul>
        <li>Profile pictures</li>
        <li>Session cover images</li>
        <li>Messages in chats</li>
        <li>Ratings and reviews</li>
      </ul>

      <h3>Device Information</h3>
      <p>We may collect:</p>
      <ul>
        <li>Device type</li>
        <li>Operating system</li>
        <li>App version</li>
        <li>Crash and analytics data</li>
      </ul>

      <h3>Subscription Information</h3>
      <p>
        If users purchase TeamFit Pro, payment information is securely handled through Apple App Store or Google Play and is never stored directly by TeamFit.
      </p>

      <h2>2. How We Use Information</h2>
      <p>We use collected information to:</p>
      <ul>
        <li>Provide app functionality</li>
        <li>Match users with activities and groups</li>
        <li>Enable chat and social features</li>
        <li>Improve user experience</li>
        <li>Send notifications and reminders</li>
        <li>Improve safety and moderation</li>
        <li>Process subscriptions</li>
        <li>Prevent fraud and abuse</li>
      </ul>

      <h2>3. Location Services</h2>
      <p>TeamFit uses location services to:</p>
      <ul>
        <li>Show nearby activities</li>
        <li>Create routes</li>
        <li>Share live location during sessions</li>
        <li>Improve navigation and maps</li>
      </ul>
      <p>Users can disable location permissions anytime in device settings.</p>

      <h2>4. Notifications</h2>
      <p>TeamFit may send push notifications for:</p>
      <ul>
        <li>Session reminders</li>
        <li>Chat messages</li>
        <li>New participants</li>
        <li>Ratings/reviews</li>
        <li>Updates and announcements</li>
      </ul>
      <p>Users can manage notifications in app settings.</p>

      <h2>5. Sharing of Information</h2>
      <p><strong>We do not sell personal data.</strong></p>
      <p>Information may only be shared:</p>
      <ul>
        <li>With other users as part of app functionality</li>
        <li>With trusted service providers</li>
        <li>If required by law</li>
        <li>To protect safety and prevent abuse</li>
      </ul>

      <h2>6. Data Storage and Security</h2>
      <p>We use industry-standard security measures to protect user data.</p>
      <p>Data is stored securely while accounts remain active.</p>
      <p>
        When users delete their account, personal information will be deleted or anonymized within a reasonable timeframe unless legally required otherwise.
      </p>

      <h2>7. Children and Minors</h2>
      <p>TeamFit is available to users under 18, but younger users should use the app with parental guidance.</p>
      <p>We do not knowingly collect sensitive data from children without appropriate consent where required by law.</p>

      <h2>8. User Rights (GDPR)</h2>
      <p>Users may:</p>
      <ul>
        <li>Access their personal data</li>
        <li>Request corrections</li>
        <li>Request deletion</li>
        <li>Withdraw consent</li>
        <li>Export account data</li>
      </ul>
      <p>Requests can be sent to: <a href="mailto:support@teamfit.dk">support@teamfit.dk</a></p>

      <h2>9. Third-Party Services and Sub-Processors</h2>
      <p>TeamFit relies on the following third-party services ("sub-processors") to operate. Each service processes only the minimum data necessary for its function, under its own privacy policy and, where applicable, a Data Processing Agreement (DPA) with TeamFit:</p>
      <ul>
        <li><strong>Stripe Payments Europe, Ltd.</strong> (Ireland) — payment processing for web subscriptions. Processes: billing name, billing email, billing address, last 4 digits of your card + card brand + expiry, IP address at time of payment, subscription status. Stripe is PCI-DSS Level 1 certified; TeamFit never sees or stores your full card number. <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></li>
        <li><strong>Resend</strong> (United States, EU DPA in place) — transactional emails (password reset, referral notifications, session reminders). Processes: email address, name, email content and delivery metadata. <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">resend.com/legal/privacy-policy</a></li>
        <li><strong>Apple Inc.</strong> — Sign-in with Apple + mobile in-app purchases (App Store). Processes: Apple ID email (optionally anonymised), device identifiers.</li>
        <li><strong>Google LLC</strong> — Sign-in with Google + mobile in-app purchases (Google Play). Processes: Google account email, name, profile picture, device identifiers.</li>
        <li><strong>OpenStreetMap Foundation</strong> / OSRM (community-run, EU) — route calculation and geocoding for cycling and running sessions. Processes: session start/end coordinates. No personal identifiers are sent.</li>
        <li><strong>MongoDB Atlas</strong> (managed database, EU region) — primary storage of application data.</li>
        <li>Cloud infrastructure (hosting, CDN, edge network) — used to serve teamfit.dk. Processes: server request logs including IP addresses, retained for operational security up to 30 days.</li>
      </ul>
      <p>A full, up-to-date list of sub-processors is available on request from <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>. Users will be notified at least 30 days before any material change (added sub-processor).</p>

      <h2>10. Changes to This Policy</h2>
      <p>We may update this Privacy Policy from time to time. Users will be notified of significant changes inside the app or website.</p>

      <h2>11. Contact</h2>
      <p>
        TeamFit<br />
        Website: <a href="https://teamfit.dk">teamfit.dk</a><br />
        Email: <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>
      </p>
    </>
  );
}

function DanishPrivacy() {
  return (
    <>
      <p>
        Velkommen til TeamFit. Dit privatliv er vigtigt for os. Denne privatlivspolitik forklarer, hvordan TeamFit indsamler, bruger, opbevarer og beskytter dine oplysninger, når du bruger vores mobilapp, hjemmeside og relaterede tjenester.
      </p>
      <p>Ved at bruge TeamFit accepterer du denne privatlivspolitik.</p>

      <h2>1. Oplysninger vi indsamler</h2>

      <h3>Kontooplysninger</h3>
      <ul>
        <li>Navn</li>
        <li>E-mailadresse</li>
        <li>Profilbillede</li>
        <li>Alder</li>
        <li>Højde og vægt (valgfrit)</li>
        <li>Fitnessniveau og sportsinteresser</li>
      </ul>

      <h3>Login-oplysninger</h3>
      <p>Når du logger ind med:</p>
      <ul>
        <li>Apple-login</li>
        <li>Google-login</li>
      </ul>
      <p>kan vi modtage grundlæggende profiloplysninger fra disse udbydere.</p>

      <h3>Positionsoplysninger</h3>
      <p>Vi indsamler:</p>
      <ul>
        <li>GPS-position</li>
        <li>Live-position under sessioner (hvis aktiveret)</li>
        <li>Rute- og positionsoplysninger</li>
      </ul>
      <p>Dette hjælper brugere med at oprette aktiviteter, finde sessioner i nærheden og navigere ruter.</p>

      <h3>Brugerindhold</h3>
      <p>Brugere kan uploade:</p>
      <ul>
        <li>Profilbilleder</li>
        <li>Coverbilleder til sessioner</li>
        <li>Beskeder i chats</li>
        <li>Bedømmelser og anmeldelser</li>
      </ul>

      <h3>Enhedsoplysninger</h3>
      <p>Vi kan indsamle:</p>
      <ul>
        <li>Enhedstype</li>
        <li>Operativsystem</li>
        <li>Appversion</li>
        <li>Crash- og analytics-data</li>
      </ul>

      <h3>Abonnementsoplysninger</h3>
      <p>
        Hvis du køber TeamFit Pro, håndteres betalingsoplysninger sikkert gennem Apple App Store eller Google Play og opbevares aldrig direkte af TeamFit.
      </p>

      <h2>2. Sådan bruger vi oplysninger</h2>
      <p>Vi bruger de indsamlede oplysninger til at:</p>
      <ul>
        <li>Levere appens funktionalitet</li>
        <li>Matche brugere med aktiviteter og grupper</li>
        <li>Muliggøre chat og sociale funktioner</li>
        <li>Forbedre brugeroplevelsen</li>
        <li>Sende notifikationer og påmindelser</li>
        <li>Forbedre sikkerhed og moderation</li>
        <li>Behandle abonnementer</li>
        <li>Forhindre svindel og misbrug</li>
      </ul>

      <h2>3. Lokationstjenester</h2>
      <p>TeamFit bruger lokationstjenester til at:</p>
      <ul>
        <li>Vise aktiviteter i nærheden</li>
        <li>Oprette ruter</li>
        <li>Dele live-position under sessioner</li>
        <li>Forbedre navigation og kort</li>
      </ul>
      <p>Du kan til enhver tid deaktivere lokationstilladelser i din enheds indstillinger.</p>

      <h2>4. Notifikationer</h2>
      <p>TeamFit kan sende push-notifikationer om:</p>
      <ul>
        <li>Sessionspåmindelser</li>
        <li>Chatbeskeder</li>
        <li>Nye deltagere</li>
        <li>Bedømmelser/anmeldelser</li>
        <li>Opdateringer og meddelelser</li>
      </ul>
      <p>Du kan administrere notifikationer i appens indstillinger.</p>

      <h2>5. Deling af oplysninger</h2>
      <p><strong>Vi sælger ikke personlige data.</strong></p>
      <p>Oplysninger kan kun deles:</p>
      <ul>
        <li>Med andre brugere som en del af appens funktionalitet</li>
        <li>Med betroede tjenesteudbydere</li>
        <li>Hvis det kræves af loven</li>
        <li>For at beskytte sikkerhed og forhindre misbrug</li>
      </ul>

      <h2>6. Dataopbevaring og sikkerhed</h2>
      <p>Vi bruger sikkerhedsforanstaltninger på branchestandard-niveau for at beskytte brugerdata.</p>
      <p>Data opbevares sikkert, så længe kontoen er aktiv.</p>
      <p>
        Når du sletter din konto, vil personlige oplysninger blive slettet eller anonymiseret inden for en rimelig tidsramme, medmindre andet er lovpligtigt.
      </p>

      <h2>7. Børn og mindreårige</h2>
      <p>TeamFit er tilgængelig for brugere under 18 år, men yngre brugere bør bruge appen under forældrenes vejledning.</p>
      <p>Vi indsamler ikke bevidst følsomme data fra børn uden passende samtykke, hvor det kræves af loven.</p>

      <h2>8. Dine rettigheder (GDPR)</h2>
      <p>Som bruger har du ret til at:</p>
      <ul>
        <li>Få adgang til dine personoplysninger</li>
        <li>Anmode om rettelser</li>
        <li>Anmode om sletning</li>
        <li>Trække dit samtykke tilbage</li>
        <li>Eksportere dine kontodata</li>
      </ul>
      <p>Anmodninger kan sendes til: <a href="mailto:support@teamfit.dk">support@teamfit.dk</a></p>

      <h2>9. Tredjepartstjenester og databehandlere</h2>
      <p>TeamFit anvender følgende tredjepartstjenester ("underdatabehandlere") for at kunne fungere. Hver tjeneste behandler kun de minimumdata, der er nødvendige for dens funktion, i henhold til dens egen privatlivspolitik og, hvor det er relevant, en databehandleraftale (DPA) med TeamFit:</p>
      <ul>
        <li><strong>Stripe Payments Europe, Ltd.</strong> (Irland) — betalingsbehandling for web-abonnementer. Behandler: faktureringsnavn, faktureringse-mail, faktureringsadresse, sidste 4 cifre af dit kort + korttype + udløbsdato, IP-adresse på betalingstidspunktet, abonnementsstatus. Stripe er PCI-DSS Level 1-certificeret; TeamFit ser eller gemmer aldrig dit fulde kortnummer. <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></li>
        <li><strong>Resend</strong> (USA, EU DPA i kraft) — transaktionsmails (nulstilling af adgangskode, henvisningsmeddelelser, sessionspåmindelser). Behandler: e-mailadresse, navn, e-mailindhold og leveringsmetadata. <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">resend.com/legal/privacy-policy</a></li>
        <li><strong>Apple Inc.</strong> — Log ind med Apple + mobile in-app-køb (App Store). Behandler: Apple ID-e-mail (evt. anonymiseret), enhedsidentifikatorer.</li>
        <li><strong>Google LLC</strong> — Log ind med Google + mobile in-app-køb (Google Play). Behandler: Google-konto-e-mail, navn, profilbillede, enhedsidentifikatorer.</li>
        <li><strong>OpenStreetMap Foundation</strong> / OSRM (community-drevet, EU) — ruteberegning og geokodning for cykel- og løbesessioner. Behandler: sessionens start-/slutkoordinater. Ingen personlige identifikatorer sendes.</li>
        <li><strong>MongoDB Atlas</strong> (managed database, EU-region) — primær opbevaring af applikationsdata.</li>
        <li>Cloud-infrastruktur (hosting, CDN, edge-netværk) — bruges til at levere teamfit.dk. Behandler: serverforespørgselslogfiler inkl. IP-adresser, opbevaret op til 30 dage af hensyn til driftsikkerhed.</li>
      </ul>
      <p>En fuld, opdateret liste over underdatabehandlere kan fås på anmodning fra <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>. Brugere vil blive underrettet mindst 30 dage før en væsentlig ændring (tilføjet underdatabehandler).</p>

      <h2>10. Ændringer af denne politik</h2>
      <p>Vi kan opdatere denne privatlivspolitik fra tid til anden. Brugere vil blive underrettet om væsentlige ændringer i appen eller på hjemmesiden.</p>

      <h2>11. Kontakt</h2>
      <p>
        TeamFit<br />
        Hjemmeside: <a href="https://teamfit.dk">teamfit.dk</a><br />
        E-mail: <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>
      </p>
    </>
  );
}
